// Helpers for turning a getUserMedia / Agora track-creation failure into a small set of
// actionable reasons plus human-facing guidance for the in-call "your camera/mic didn't
// start" banner.
//
// Agora (agora-rtc-sdk-ng) wraps the browser DOMException in an AgoraRTCError whose `.code`
// is a stable string (e.g. PERMISSION_DENIED, NOT_READABLE, NOT_FOUND), while the underlying
// browser error is a DOMException with a `.name` (NotAllowedError, NotReadableError, ...).
// We inspect both so classification is robust across browsers and SDK versions.

export const MEDIA_ERROR_REASONS = Object.freeze({
  permission: 'permission',
  inUse: 'inUse',
  notFound: 'notFound',
  overconstrained: 'overconstrained',
  unknown: 'unknown',
})

export function classifyMediaError(error) {
  const haystack = [error?.code, error?.name, error?.message]
    .filter(Boolean)
    .join(' ')
    .toUpperCase()

  if (!haystack) {
    return MEDIA_ERROR_REASONS.unknown
  }

  if (
    haystack.includes('PERMISSION_DENIED') ||
    haystack.includes('NOTALLOWED') ||
    haystack.includes('PERMISSIONDENIED') ||
    haystack.includes('SECURITYERROR')
  ) {
    return MEDIA_ERROR_REASONS.permission
  }

  if (
    haystack.includes('NOT_FOUND') ||
    haystack.includes('NOTFOUND') ||
    haystack.includes('DEVICES_NOT_FOUND') ||
    haystack.includes('DEVICE_NOT_FOUND')
  ) {
    return MEDIA_ERROR_REASONS.notFound
  }

  if (
    haystack.includes('NOT_READABLE') ||
    haystack.includes('NOTREADABLE') ||
    haystack.includes('TRACKSTART') ||
    haystack.includes('ABORTERROR') ||
    haystack.includes('COULD NOT START') ||
    haystack.includes('DEVICE_IN_USE')
  ) {
    return MEDIA_ERROR_REASONS.inUse
  }

  if (
    haystack.includes('OVERCONSTRAINED') ||
    haystack.includes('CONSTRAINT_NOT_SATISFIED') ||
    haystack.includes('CONSTRAINTNOTSATISFIED')
  ) {
    return MEDIA_ERROR_REASONS.overconstrained
  }

  return MEDIA_ERROR_REASONS.unknown
}

// Reasons where an immediate retry has a good chance of succeeding — either because the
// device just needs a moment to be released, or because we can fall back to the default
// device. Permission / not-found need the user to act first, so we don't spin on those.
export function isRetryableMediaError(reason) {
  return (
    reason === MEDIA_ERROR_REASONS.inUse ||
    reason === MEDIA_ERROR_REASONS.overconstrained ||
    reason === MEDIA_ERROR_REASONS.unknown
  )
}

export function isMacPlatform(nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav) {
    return false
  }

  const signature = `${nav.platform || ''} ${nav.userAgent || ''}`.toUpperCase()

  // iPadOS/iOS can report "Mac" in some UA strings but need different guidance, so exclude them.
  if (signature.includes('IPHONE') || signature.includes('IPAD')) {
    return false
  }

  return signature.includes('MAC')
}

export function isStandaloneDisplayMode(win = typeof window !== 'undefined' ? window : null) {
  if (!win) {
    return false
  }

  try {
    return (
      win.matchMedia?.('(display-mode: standalone)')?.matches === true ||
      win.navigator?.standalone === true
    )
  } catch (error) {
    return false
  }
}

const REASON_PRIORITY = [
  MEDIA_ERROR_REASONS.permission,
  MEDIA_ERROR_REASONS.notFound,
  MEDIA_ERROR_REASONS.inUse,
  MEDIA_ERROR_REASONS.overconstrained,
  MEDIA_ERROR_REASONS.unknown,
]

function describeKinds(kinds) {
  const hasCamera = kinds.includes('camera')
  const hasMicrophone = kinds.includes('microphone')

  if (hasCamera && hasMicrophone) {
    return { noun: 'camera and microphone', Noun: 'Camera and microphone', areOrIs: 'are' }
  }

  if (hasCamera) {
    return { noun: 'camera', Noun: 'Camera', areOrIs: 'is' }
  }

  return { noun: 'microphone', Noun: 'Microphone', areOrIs: 'is' }
}

// Builds the notice shown in the in-call banner. `failing` is a list of
// { kind: 'camera' | 'microphone', reason }. Returns null when there's nothing to show.
export function buildDeviceErrorNotice(failing, { isMac = false, isStandalone = false } = {}) {
  if (!Array.isArray(failing) || failing.length === 0) {
    return null
  }

  const kinds = failing.map((item) => item.kind)
  const reason =
    REASON_PRIORITY.find((candidate) => failing.some((item) => item.reason === candidate)) ||
    MEDIA_ERROR_REASONS.unknown
  const { noun, Noun, areOrIs } = describeKinds(kinds)

  const remedy = []
  let title
  let detail

  if (reason === MEDIA_ERROR_REASONS.permission) {
    title = `${Noun} ${areOrIs} blocked`
    detail = `This call isn't allowed to use your ${noun}. Your device is fine — the browser just needs permission.`
    if (isStandalone) {
      remedy.push(`Open this app's site settings and set ${noun} access to "Allow".`)
    } else {
      remedy.push(
        `Click the camera / lock icon at the left of the address bar, set ${noun} to "Allow", then reload.`,
      )
    }
    if (isMac) {
      remedy.push(
        'On Mac, also open System Settings → Privacy & Security → Camera / Microphone and make sure your browser is enabled.',
      )
    }
  } else if (reason === MEDIA_ERROR_REASONS.inUse) {
    title = `Your ${noun} ${areOrIs} busy`
    detail = `Another app or browser tab is using your ${noun}, so this call can't turn it on.`
    remedy.push(
      'Close other video apps (Zoom, Meet, FaceTime, Photo Booth) and any other tabs using the camera, then press Try again.',
    )
    if (isMac) {
      remedy.push(`Just plugged or unplugged a device? Press Try again — we'll reconnect to your default ${noun}.`)
    }
  } else if (reason === MEDIA_ERROR_REASONS.notFound) {
    title = `No ${noun} found`
    detail = `We couldn't find a ${noun} on this device.`
    remedy.push(`Connect a ${noun} and press Try again.`)
    if (isMac) {
      remedy.push('On Mac, check System Settings → Privacy & Security → Camera / Microphone for your browser.')
    }
  } else {
    // overconstrained / unknown
    title = `We couldn't start your ${noun}`
    detail = `Something interrupted access to your ${noun}.`
    remedy.push("Press Try again — we'll reconnect using your default device.")
    remedy.push('If it keeps happening, close other apps using the camera / mic or reload the page.')
  }

  return {
    reason,
    kinds,
    title,
    detail,
    remedy,
    canRetry: true,
  }
}
